import { Type } from '@angular/core';
import { JsonSchema } from '@jsonforms/core';
import { GridSize } from '../layout/dashboard-layout';

export type DashboardWidgetFrame = 'card' | 'bare';

export const UNAVAILABLE_WIDGET_TITLE = 'Dashboard.Frame.UnavailableTitle';

/** Size limits in grid cells */
export interface DashboardWidgetSize {
  default: GridSize;
  min?: GridSize;
  max?: GridSize;
}

/** Type-only: carries the dashboard state a widget uses, so `provideDashboard` can check it */
declare const STATE_USED: unique symbol;

/**
 * What Balsa registers for a widget type. Layouts refer to it by `type`. `State` is the part of the dashboard
 * state the widget reads with `injectDashboardState<State>()`; a widget that uses none can run on any dashboard.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DashboardWidgetDefinition<S = unknown, State extends object = {}> {
  readonly [STATE_USED]?: State;
  type: string;
  /** A translation key, or text shown as is */
  title: string;
  description?: string;
  /** Material icon name, shown in the catalog */
  icon: string;
  /** The body component; it injects its handle with `injectDashboardWidget()` */
  loadComponent: () => Promise<Type<unknown>>;
  /** 'card' (default) draws the standard frame; 'bare' draws only edit affordances */
  frame?: DashboardWidgetFrame;
  size: DashboardWidgetSize;
  defaultSettings?: S;
  /** Validates layout settings, and drives the generated settings form unless `loadSettingsComponent` is set */
  settingsSchema?: JsonSchema;
  /** Replaces the generated settings form; receives a handle for the widget, with `editing` true */
  loadSettingsComponent?: () => Promise<Type<DashboardSettingsComponent<S>>>;
  /** All are required to see or add the widget */
  permissions?: string[];
  /** Defaults to 1; use Infinity for unlimited */
  maxInstances?: number;
}

export function maxInstancesOf(definition: Pick<DashboardWidgetDefinition, 'maxInstances'>): number {
  return definition.maxInstances ?? 1;
}

export interface DashboardSettingsComponent<S = unknown> {
  /** Called by the settings dialog on Save; returns the settings to store */
  collectSettings(): S;
}
